import {
    ADDRESS,
    BUILDING,
    CONTACT_EMAIL,
    FAX,
    PHONE,
    POSTAL_CODE,
    PREFECTURE,
    REMARKS,
    WAREHOUSE_NAME_FURIGANA,
    WAREHOUSE_NAME_KANJI
} from '@/pages/admin/warehouse/components/WarehouseForm/constants';
import { BASE_URL_API } from '@constants/base';
import { get, post } from '@utils/ajax';

/**
 * Warehouses Service
 * Handles all warehouse-related API calls
 */
const warehousesService = (() => {
    // Get warehouse list
    const getWarehouseList = async params => {
        try {
            const response = await get(`${BASE_URL_API}/main/warehouse/list.php`, params, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Get warehouse detail
    const getWarehouseDetail = async (mng_id, warehouse_id) => {
        try {
            return await get(
                `${BASE_URL_API}/main/warehouse/detail.php`,
                {
                    mng_id,
                    warehouse_id
                },
                {
                    showError: true
                }
            );
        } catch (error) {
            return error;
        }
    };

    // Get warehouse by ID (for edit page) - matches expected interface
    const getWarehouse = async ({ id, mngId }) => {
        try {
            // If mngId is provided, use it, otherwise try to get it from URL params or state
            const companyId = mngId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('companyId') : null);

            if (!companyId) {
                throw new Error('倉庫管理会社IDが必要です。');
            }

            const response = await getWarehouseDetail(companyId, id);

            if (response?.status === 'success' && response?.data) {
                const transformedData = {
                    [WAREHOUSE_NAME_KANJI]: response.data.mWrhTxtName || '',
                    [WAREHOUSE_NAME_FURIGANA]: response.data.mWrhTxtNameKana || '',
                    [CONTACT_EMAIL]: response.data.mWrhTxtMailAddress || '',
                    [POSTAL_CODE]: response.data.mWrhTxtZip || '',
                    [PHONE]: response.data.mWrhTxtTel || '',
                    [FAX]: response.data.mWrhTxtFax || '',
                    [ADDRESS]: response.data.mWrhTxtAddress || '',
                    [BUILDING]: response.data.mWrhTxtAddressSub || '',
                    [REMARKS]: response.data.mWrhTxtNotes || '',
                    [PREFECTURE]: response.data.mWrhTxtPref || '',
                    // Additional metadata
                    id: response.data.mWrhIntId,
                    mngId: response.data.mWrhIntMngId,
                    companyId: response.data.mMngIntId,
                    companyName: response.data.mComTxtCompany || '',
                    createdAt: response.data.mWrhDtmReg,
                    updatedAt: response.data.mWrhDtmUpd
                };

                return {
                    status: 'success',
                    data: transformedData,
                    message: '倉庫データを正常に取得しました。'
                };
            } else {
                return {
                    status: 'error',
                    message: response?.message || '倉庫データの取得に失敗しました。'
                };
            }
        } catch (error) {
            return {
                status: 'error',
                message: error.message || '倉庫データの取得中にエラーが発生しました。'
            };
        }
    };

    // Update warehouse
    const updateWarehouse = async (warehouseId, mngId, payload) => {
        try {
            const warehouseData = {
                m_wrh_txt_name: payload[WAREHOUSE_NAME_KANJI],
                m_wrh_txt_name_kana: payload[WAREHOUSE_NAME_FURIGANA],
                m_wrh_txt_mail_address: payload[CONTACT_EMAIL] || '',
                m_wrh_txt_zip: payload[POSTAL_CODE],
                m_wrh_txt_address: payload[ADDRESS] || '',
                m_wrh_txt_address_sub: payload[BUILDING] || '',
                m_wrh_txt_tel: payload[PHONE],
                m_wrh_txt_fax: payload[FAX] || '',
                m_wrh_txt_notes: payload[REMARKS] || ''
            };

            return await post(
                `${BASE_URL_API}/main/warehouse/update.php`,
                {
                    method: 'update',
                    warehouse_id: warehouseId,
                    mng_id: mngId,
                    m_warehouse: warehouseData
                },
                {
                    showError: true
                }
            );
        } catch (error) {
            return error;
        }
    };

    // Create new warehouse (insert API)
    const createWarehouse = async payload => {
        try {
            // Prepare warehouse data according to backend API expectations
            const warehouseData = {
                m_wrh_txt_name: payload[WAREHOUSE_NAME_KANJI],
                m_wrh_txt_name_kana: payload[WAREHOUSE_NAME_FURIGANA],
                m_wrh_txt_mail_address: payload[CONTACT_EMAIL] || '', // Add email field
                m_wrh_txt_zip: payload[POSTAL_CODE],
                m_wrh_txt_address: payload[ADDRESS] || '',
                m_wrh_txt_address_sub: payload[BUILDING] || '', // Building as address_sub
                m_wrh_txt_tel: payload[PHONE],
                m_wrh_txt_fax: payload[FAX] || '',
                m_wrh_txt_notes: payload[REMARKS] || '' // Use m_wrh_txt_notes instead of m_wrh_txt_remarks
            };

            // Prepare API payload matching backend expectations
            const apiPayload = {
                method: 'insert',
                mng_id: payload.mng_id,
                m_warehouse: warehouseData
            };

            return await post(`${BASE_URL_API}/main/warehouse/insert.php`, apiPayload, {
                showError: true
            });
        } catch (error) {
            return error;
        }
    };

    // Delete warehouse
    const deleteWarehouse = async ({ id, mngId }) => {
        try {
            if (!id || !mngId) {
                throw new Error('倉庫IDと倉庫管理会社IDが必要です。');
            }

            return await post(
                `${BASE_URL_API}/main/warehouse/delete.php`,
                {
                    method: 'delete',
                    wrh_id: id,
                    mng_id: mngId
                },
                {
                    showError: true
                }
            );
        } catch (error) {
            return error;
        }
    };

    return {
        getWarehouseList,
        getWarehouseDetail,
        getWarehouse,
        updateWarehouse,
        createWarehouse,
        deleteWarehouse
    };
})();

export default warehousesService;
